export type SbrBlogCategoryTone = 'teal' | 'blue' | 'purple';

export type SbrBlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  publishedAt: string;
  categoryLabel: string;
  categoryTone: SbrBlogCategoryTone;
  readMinutes: number;
};

export type SbrBlogPost = SbrBlogPostSummary & {
  contentHtml: string;
};

const POSTS_BASE = `${process.env.PUBLIC_URL}/blog/posts`;

function localizeBlogHtmlAssets(html: string): string {
  const base = process.env.PUBLIC_URL || '';
  let out = html
    .replace(/https?:\/\/(?:www\.)?sbr2th\.com\/blog\/([^/"'\s?#]+)\/?/gi, '/blog/$1')
    .replace(/https?:\/\/(?:calendar\.app\.google|calender\.add\.gugle)[^\s"'\\)]+/gi, '/book-a-call');
  if (base) {
    out = out.replace(/(\s(?:src|href)=["'])\/blog\//g, `$1${base}/blog/`);
    out = out.replace(/(\shref=["'])\/book-a-call/g, `$1${base}/book-a-call`);
  }
  return out;
}

/** Resolve featured/inline blog image paths stored in JSON (local or absolute). */
export function resolveBlogImageUrl(imageUrl: string | undefined): string {
  const url = imageUrl?.trim();
  if (!url || url === '#' || url.startsWith('data:,')) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.PUBLIC_URL || '';
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
}

export const SBR_BLOG_INDEX_PATH = '/blog';

export function blogPostPath(slug: string): string {
  return `/blog/${slug}`;
}

export function hasBlogPostImage(imageUrl: string | undefined): boolean {
  const url = imageUrl?.trim();
  return Boolean(url && url !== '#' && !url.startsWith('data:,'));
}

function decodeHtml(text: string): string {
  return text
    .replace(/&#8230;/g, '…')
    .replace(/&#8217;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&#038;/g, '&')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeSummary(raw: SbrBlogPostSummary): SbrBlogPostSummary {
  return {
    ...raw,
    title: decodeHtml(raw.title),
    excerpt: decodeHtml(raw.excerpt),
  };
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Blog fetch failed (${res.status})`);
  return res.json() as Promise<T>;
}

/** All blog posts (newest first), stored under public/blog/posts/. */
export async function fetchLatestSbrBlogPosts(limit?: number): Promise<SbrBlogPostSummary[]> {
  const list = await fetchJson<SbrBlogPostSummary[]>(`${POSTS_BASE}/index.json`);
  const normalized = list.map(normalizeSummary);
  return limit ? normalized.slice(0, limit) : normalized;
}

/** Full article body for on-site reading. */
export async function fetchSbrBlogPost(slug: string): Promise<SbrBlogPost | null> {
  try {
    const raw = await fetchJson<SbrBlogPost>(`${POSTS_BASE}/${slug}.json`);
    return {
      ...normalizeSummary(raw),
      contentHtml: localizeBlogHtmlAssets(raw.contentHtml),
    };
  } catch {
    return null;
  }
}

export function formatBlogDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}
